import React from 'react';
import PropTypes from 'prop-types';
import clone from 'clone';

import {connectAppEnv} from '../../../app/app-env';
import {connectModuleEnv} from '../../oe-module-env';
import {frefFromRef, frefToRef} from '../../../lib/react/higher-order-components';
import {oeInterfaceManager} from '../../../react-oe/oe-interface';
import OEInterfaceAdapter from '../../../react-oe/oe-interface-adapter';
import {OEShowInitiallyFlags} from '../../../lib/oe-types';
import {OECapBarPropTypes, OECapBarConfigFactory} from './oe-cap-bar-model';
import OECapBarFactory from './oe-cap-bar-factory';
import OEResizeObserver from '../../../lib/components/oe-resize-observer';
import OEToolbox from '../../../lib/tools/oe-toolbox';
import OEAnimateHelper from '../../../lib/animation/oe-animate-helper';
import retardUpdate from '../../../lib/react/update-retarder';

export class OECapBar extends React.PureComponent {

    constructor(props) {
        super(props);

        this.props.appComponent.capBar = this;

        this.oe = oeInterfaceManager.getInterface(this.props.moduleId);

        this.show_ = OEShowInitiallyFlags.resolve(this.props.config.showInitially).show;

        this.size = {w: 0, h: 0};
        this.offsets = this.determineOffsets();
        this.edgeOffsets = this.determineEdgeOffsets();

        this.state = {
            show: this.show_,
            offsets: clone(this.offsets),
            edgeOffsets: clone(this.edgeOffsets)
        };

        this.onResize = this.onResize.bind(this);
    }

    show(show, animated)    {
        show = !!show;
        if(this.show_ == show)   return;
        this.show_ = show;

        retardUpdate(this, () => {
            this.setState({show: show});
            this.updateOffsets(animated);
            this.updateEdgeOffsets(animated);
        });
    }

    componentWillReceiveProps(nextProps) {
        if(this.props.config.available !== nextProps.config.available || this.props.opaque !== nextProps.opaque) {
            retardUpdate(this, () => {
                this.updateOffsets(false, nextProps);
                this.updateEdgeOffsets(false, nextProps);
            });
        }
    }

    onResize(sender, size)    {
        if(OEToolbox.shallowEqual(this.size, size)) return;
        this.size = size;

        retardUpdate(this, () => {
            this.updateOffsets(false);
            this.updateEdgeOffsets(false);
        });

        if(this.props.uiStateManager)   this.props.uiStateManager.updateState({capBar: {size : {$set: clone(size)}}});      // opaque instance does not have uiStateManager (not in module environment)
        if(this.props.onSizeChanged)  this.props.onSizeChanged(this, clone(size));
    }

    determineOffsets(animated, props)  {
        animated = !!animated;
        props = props || this.props;

        let offsets = {
            top: -this.size.h, right: 0, bottom: 0, left: 0,
            animate: !!animated
        }

        if(!props.opaque && props.config.available && this.show_) offsets.top = 0;

        return offsets;
    }

    determineEdgeOffsets(animated, props)  {
        animated = !!animated;
        props = props || this.props;

        let edgeOffsets = {
            top: 0, right: 0, bottom: 0, left: 0,
            animate: animated
        }

        if(!props.opaque && props.config.available && this.show_) edgeOffsets.top = this.size.h;

        return edgeOffsets;
    }

    updateOffsets(animated, props) {
        props = props || this.props;
        let offsets = this.determineOffsets(animated, props);
        if(OEAnimateHelper.offsetEqual(this.offsets, offsets))  return;
        this.offsets = offsets;
        this.setState({offsets: clone(offsets)});
    }

    updateEdgeOffsets(animated, props) {
        props = props || this.props;
        let edgeOffsets = this.determineEdgeOffsets(animated, props);
        if(OEAnimateHelper.offsetEqual(this.edgeOffsets, edgeOffsets))  return;
        this.edgeOffsets = edgeOffsets;
        this.setState({edgeOffsets: clone(edgeOffsets)});
        props.uiStateManager.updateState({capBar: {edgeOffsets : {$set: clone(edgeOffsets)}}});       // opaque instance does not have uiStateManager (not in module environment)
        if(props.onEdgeOffsetsChanged)  props.onEdgeOffsetsChanged(this, clone(edgeOffsets));
    }

    updateState(released)   {
        let show;
        let resolved = OEShowInitiallyFlags.resolve(this.props.config.showInitially);
        if(!resolved.onlyAtStartUp || !this.showInitiallySet) show = resolved.show;
        this.showInitiallySet = true;
        if(show !== undefined)    this.show(show, true);

        if(!this.oe.isReady() || released === true)  {
            return;
        }
    }

    render() {
        const {children, ...rest} = this.props;

        const style = {
            top: this.state.offsets.top,
            transition: OEAnimateHelper.transitionForInsets(this.state.offsets.animate, undefined, {top: true})
        };

        return (
            <React.Fragment>
                <OEInterfaceAdapter moduleId={this.props.moduleId} receiver={this}/>
                <div
                    className={'cap-bar' + (this.props.opaque ? ' opaque' : ' transparent') + (!this.state.show ? ' hide' : '')}
                    style={style}
                >
                    <OECapBarFactory {...rest}/>
                    {children}
                    <OEResizeObserver onResize={this.onResize} />
                </div>
            </React.Fragment>
        );
    }
}

OECapBar.defaultProps = {
    moduleId:               '',
    opaque:                 false,
    config:                 OECapBarConfigFactory.buildStdConfig()
};

OECapBar.propTypes = {
    moduleId:               PropTypes.string,
    opaque:                 PropTypes.bool,
    config:                 OECapBarPropTypes.config,
    onSizeChanged:          PropTypes.func,
    onEdgeOffsetsChanged:   PropTypes.func
};

export default frefFromRef(connectAppEnv((env) => {
    return {
        appComponent:       env.component,
        config:             env.config.module.uiLayerConfig.capBar
    };
})(connectModuleEnv((env) => {
    return {
        uiStateManager:     env.component.uiStateManager,
    };
})(frefToRef(OECapBar))));